import React, { useState, useEffect } from 'react';
import { RealtimeDBService } from '../../../../firebase/unifiedDataService';
import { USER_ROLES } from '../../../../config/userRoles.js';
import { Button } from '../../../../components/ui/BaseComponents';
import { AlertBanner } from '../../../../components/ui/CommonComponents';
import { ConfirmDialog } from '../../../../components/ui/ConfirmDialog';
import { Logger } from '../../../../utils/logger';

const log = Logger.create('ManageMembersModal');

/**
 * ManageMembersModal - Modal to manage course members
 * 
 * Features:
 * - List all members with their roles
 * - Change member role (TEACHER ↔ STUDENT)
 * - Remove member (except owner)
 * - Add new members from existing users
 */
function ManageMembersModal({ isOpen, onClose, course, onUpdate }) {
  const [members, setMembers] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [selectedUserToAdd, setSelectedUserToAdd] = useState('');
  const [removeDialogOpen, setRemoveDialogOpen] = useState(false);
  const [memberToRemove, setMemberToRemove] = useState(null);

  useEffect(() => {
    if (isOpen && course) {
      fetchMembersAndUsers();
    }
  }, [isOpen, course]);

  const fetchMembersAndUsers = async () => {
    setLoading(true);
    setError(null);
    try {
      // Fetch all users using RealtimeDBService
      const usersData = await RealtimeDBService.get('users') || {};

      // Transform members data
      const membersArray = Object.entries(course.members || {}).map(([userId, memberData]) => {
        const userData = usersData[userId] || {};
        return {
          id: userId,
          displayName: userData.displayName || userData.email?.split('@')[0] || 'Unbekannt',
          email: userData.email || '',
          role: memberData.role || USER_ROLES.STUDENT,
          joinedAt: memberData.joinedAt || Date.now(),
          isOwner: userId === course.owner
        };
      });

      // Sort: owner first, then by name
      membersArray.sort((a, b) => {
        if (a.isOwner) return -1;
        if (b.isOwner) return 1;
        return a.displayName.localeCompare(b.displayName);
      });

      setMembers(membersArray);

      // Filter users not in course
      const memberIds = Object.keys(course.members || {});
      const availableUsers = Object.entries(usersData)
        .filter(([userId]) => !memberIds.includes(userId))
        .map(([userId, userData]) => ({
          id: userId,
          displayName: userData.displayName || userData.email?.split('@')[0] || 'Unbekannt',
          email: userData.email || ''
        }))
        .sort((a, b) => a.displayName.localeCompare(b.displayName));

      setAllUsers(availableUsers);
    } catch (err) {
      log.error('Error fetching members:', err);
      setError('Fehler beim Laden der Mitglieder');
    } finally {
      setLoading(false);
    }
  };

  // Change member role
  const handleRoleChange = async (memberId, newRole) => {
    try {
      await RealtimeDBService.update(`courses/${course.id}/members/${memberId}`, {
        role: newRole
      });

      setSuccess('Rolle geändert');
      setTimeout(() => setSuccess(null), 2000);

      // Update local state
      setMembers(prev => prev.map(m => 
        m.id === memberId ? { ...m, role: newRole } : m
      ));

      if (onUpdate) onUpdate();
    } catch (err) {
      log.error('Error changing role:', err);
      setError('Fehler beim Ändern der Rolle');
    }
  };

  // Remove member
  const handleRemoveMember = async () => {
    if (!memberToRemove) return;

    try {
      await RealtimeDBService.remove(`courses/${course.id}/members/${memberToRemove.id}`);

      setSuccess('Mitglied entfernt');
      setTimeout(() => setSuccess(null), 2000);

      // Refresh data
      await fetchMembersAndUsers();

      if (onUpdate) onUpdate();
    } catch (err) {
      log.error('Error removing member:', err);
      setError('Fehler beim Entfernen des Mitglieds');
    } finally {
      setMemberToRemove(null);
      setRemoveDialogOpen(false);
    }
  };

  const openRemoveDialog = (member) => {
    setMemberToRemove(member);
    setRemoveDialogOpen(true);
  };

  // Add member
  const handleAddMember = async () => {
    if (!selectedUserToAdd) return;

    try {
      await RealtimeDBService.update(`courses/${course.id}/members/${selectedUserToAdd}`, {
        role: USER_ROLES.STUDENT,
        joinedAt: Date.now()
      });

      setSuccess('Mitglied hinzugefügt');
      setTimeout(() => setSuccess(null), 2000);
      setSelectedUserToAdd('');

      // Refresh data
      await fetchMembersAndUsers();

      if (onUpdate) onUpdate();
    } catch (err) {
      log.error('Error adding member:', err);
      setError('Fehler beim Hinzufügen des Mitglieds');
    }
  };

  // Format date
  const formatDate = (timestamp) => {
    if (!timestamp) return '-';
    const date = new Date(timestamp);
    return date.toLocaleDateString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: '2-digit'
    });
  };

  if (!isOpen) return null;

  return (
    <div 
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 dark:bg-black/70 p-4"
      onClick={onClose}
    >
      <div 
        className="bg-background dark:bg-card border border-border rounded-lg shadow-lg max-w-3xl w-full max-h-[80vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="p-6 border-b border-border">
          <h3 className="text-xl font-semibold text-foreground">
            Mitglieder von "{course?.name}"
          </h3>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {/* Alert Messages */}
          {error && (
            <AlertBanner variant="danger" className="mb-4">
              {error}
            </AlertBanner>
          )}
          {success && (
            <AlertBanner variant="success" className="mb-4">
              {success}
            </AlertBanner>
          )}

          {/* Loading State */}
          {loading && (
            <p className="text-center py-8 text-muted-foreground">Lade Mitglieder...</p>
          )}

          {/* Members Table */}
          {!loading && members.length > 0 && (
            <div className="overflow-x-auto mb-6">
              <table className="w-full text-sm">
                <thead className="border-b border-border">
                  <tr className="bg-muted/50">
                    <th className="text-left py-3 px-4 font-medium">Name</th>
                    <th className="text-left py-3 px-4 font-medium">Email</th>
                    <th className="text-center py-3 px-4 font-medium">Rolle</th>
                    <th className="text-center py-3 px-4 font-medium">Beigetreten</th>
                    <th className="text-center py-3 px-4 font-medium">Aktionen</th>
                  </tr>
                </thead>
                <tbody>
                  {members.map(member => (
                    <tr 
                      key={member.id}
                      className="border-b border-border hover:bg-muted/50 theme-transition"
                    >
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-foreground">
                            {member.displayName}
                          </span>
                          {member.isOwner && (
                            <span className="px-2 py-0.5 text-xs rounded-full bg-primary/10 text-primary border border-primary/20">
                              Owner
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="py-3 px-4 text-xs text-muted-foreground">
                        {member.email}
                      </td>
                      <td className="py-3 px-4 text-center">
                        {member.isOwner ? (
                          <span className="text-muted-foreground text-sm">
                            {member.role}
                          </span>
                        ) : (
                          <select
                            value={member.role}
                            onChange={(e) => handleRoleChange(member.id, e.target.value)}
                            className="px-2 py-1 border border-border rounded bg-background text-foreground text-xs"
                          >
                            <option value={USER_ROLES.STUDENT}>STUDENT</option>
                            <option value={USER_ROLES.TEACHER}>TEACHER</option>
                          </select>
                        )}
                      </td>
                      <td className="py-3 px-4 text-center text-xs text-muted-foreground">
                        {formatDate(member.joinedAt)}
                      </td>
                      <td className="py-3 px-4 text-center">
                        {!member.isOwner && (
                          <Button
                            variant="destructive"
                            size="sm"
                            onClick={() => openRemoveDialog(member)}
                          >
                            Entfernen
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Add Member Section */}
          {!loading && allUsers.length > 0 && (
            <div className="border-t border-border pt-4">
              <h4 className="text-sm font-medium text-foreground mb-3">
                Mitglied hinzufügen
              </h4>
              <div className="flex gap-2">
                <select
                  value={selectedUserToAdd}
                  onChange={(e) => setSelectedUserToAdd(e.target.value)}
                  className="flex-1 px-4 py-2 border border-border rounded-lg bg-background text-foreground"
                >
                  <option value="">Nutzer auswählen...</option>
                  {allUsers.map(user => (
                    <option key={user.id} value={user.id}>
                      {user.displayName} ({user.email})
                    </option>
                  ))}
                </select>
                <Button
                  onClick={handleAddMember}
                  disabled={!selectedUserToAdd}
                >
                  Hinzufügen
                </Button>
              </div>
            </div>
          )}

          {/* No Available Users */}
          {!loading && allUsers.length === 0 && (
            <p className="text-sm text-muted-foreground text-center py-4 border-t border-border">
              Alle Nutzer sind bereits Mitglied dieses Kurses
            </p>
          )}
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-border flex justify-end">
          <Button onClick={onClose}>
            Schließen
          </Button>
        </div>

        {/* Remove Confirmation Dialog */}
        <ConfirmDialog
          isOpen={removeDialogOpen}
          onClose={() => {
            setRemoveDialogOpen(false);
            setMemberToRemove(null);
          }}
          onConfirm={handleRemoveMember}
          title="Mitglied entfernen?"
          message={`"${memberToRemove?.displayName}" wird aus dem Kurs entfernt.\n\nDie Person kann sich über den Einladungslink erneut anmelden.`}
          confirmLabel="Entfernen"
          variant="warning"
        />
      </div>
    </div>
  );
}

export default ManageMembersModal;
